import { useState, useCallback } from 'react';
import React from 'react';
import { mentionSystem, type MentionContext, type MentionSegment } from '@/services/ai/mentions';

export interface UseMentionInputDeps {
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

export interface UseMentionInputReturn {
  showMentionAutocomplete: boolean;
  setShowMentionAutocomplete: React.Dispatch<React.SetStateAction<boolean>>;
  activeMentionContext: MentionContext | null;
  setActiveMentionContext: React.Dispatch<React.SetStateAction<MentionContext | null>>;
  cursorPosition: number;
  setCursorPosition: React.Dispatch<React.SetStateAction<number>>;
  handleSelectMention: (reference: string, segment: MentionSegment | null) => void;
  detectAndUpdateMention: (value: string, cursorPos: number) => void;
}

export function useMentionInput(deps: UseMentionInputDeps): UseMentionInputReturn {
  const { inputValue, setInputValue, inputRef } = deps;

  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [activeMentionContext, setActiveMentionContext] = useState<MentionContext | null>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Detect mention at cursor position and update autocomplete state
  function detectAndUpdateMention(value: string, cursorPos: number) {
    const context = mentionSystem.detectMention(value, cursorPos);
    if (context) {
      setShowMentionAutocomplete(true);
      setActiveMentionContext(context);
    } else {
      setShowMentionAutocomplete(false);
      setActiveMentionContext(null);
    }
  }

  const handleSelectMention = useCallback(
    (reference: string, segment: MentionSegment | null) => {
      if (!activeMentionContext) {
        setShowMentionAutocomplete(false);
        return;
      }

      // Escape pressed - close without selection
      if (!segment) {
        setShowMentionAutocomplete(false);
        setActiveMentionContext(null);
        inputRef.current?.focus();
        return;
      }

      // Replace only the mention portion
      const { startPosition, endPosition } = activeMentionContext;
      const newValue =
        inputValue.substring(0, startPosition) +
        reference +
        inputValue.substring(endPosition);

      setInputValue(newValue);

      // Handle drill-down for items with children
      if (segment.hasChildren) {
        const newCursorPos = startPosition + reference.length;
        setCursorPosition(newCursorPos);
        const newContext = mentionSystem.detectMention(newValue, newCursorPos);
        setActiveMentionContext(newContext);
        // Keep autocomplete open for drilling down
      } else {
        setShowMentionAutocomplete(false);
        setActiveMentionContext(null);
      }

      inputRef.current?.focus();
    },
    [activeMentionContext, inputValue]
  );

  return {
    showMentionAutocomplete,
    setShowMentionAutocomplete,
    activeMentionContext,
    setActiveMentionContext,
    cursorPosition,
    setCursorPosition,
    handleSelectMention,
    detectAndUpdateMention,
  };
}
